import React, { useState, useEffect } from 'react';
import { MediaItem } from '../../api/structs/media';
import Modal from './Modal';
import { Button } from '../ui';
import API from '../../api/api';
import { useToast } from '../../hooks/useToast';
import ModelsAPI from '../../api/models';
import { AIModel, usdToCredits } from '../../api/structs/model';
import { useCostDisplay } from '../../hooks/useCostDisplay';

interface LipSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  clipId: string;
  video: MediaItem;
  audios: MediaItem[];
  onSuccess: () => void;
}

type Tab = 'audio' | 'speech';

const VOICES = ['emily','james','isabella','liam','chloe','adrian','harper','ava','sophia','julia','mason','jack','oliver','ethan'];
const pickRandomVoice = () => VOICES[Math.floor(Math.random() * VOICES.length)];

const LipSyncModal: React.FC<LipSyncModalProps> = ({
  isOpen,
  onClose,
  clipId,
  video,
  audios,
  onSuccess,
}) => {
  const toast = useToast();
  const display = useCostDisplay();
  const [lipSyncCostCredits, setLipSyncCostCredits] = React.useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    ModelsAPI.getVideoModels('runware').catch(() => [] as AIModel[]).then((models) => {
      const lipSyncModels = models.filter((m) => m['subtype'] === 'lip_sync');
      if (lipSyncModels.length === 0) return;
      const model = lipSyncModels[0];
      const perSecond = model.pricing?.per_second;
      if (!perSecond) return;
      const duration = video.output_spec?.duration ?? (video.metadata?.duration as number | undefined) ?? 5;
      const credits = usdToCredits(parseFloat(perSecond) * duration);
      if (!cancelled) setLipSyncCostCredits(credits > 0 ? credits : null);
    });
    return () => { cancelled = true; };
  }, [isOpen, video.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [tab, setTab] = useState<Tab>('speech');
  const [text, setText] = useState<string>(
    typeof video.metadata?.text === 'string' ? video.metadata.text : ''
  );
  const [voice, setVoice] = useState(() => pickRandomVoice());

  useEffect(() => {
    setText(typeof video.metadata?.text === 'string' ? video.metadata.text : '');
  }, [video.id, video.metadata?.text]);
  const [speed, setSpeed] = useState(1.0);
  const [pitch, setPitch] = useState(0.0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const readyAudios = audios.filter(
    (a) => !a.file_url?.includes('_waiting') && !a.file_url?.includes('_failed') && !a.file_url?.includes('_failed.png')
  );

  const [audioMediaId, setAudioMediaId] = useState('');

  useEffect(() => {
    const matching = readyAudios.find(
      (a) => a.metadata?.scene_id === video.metadata?.scene_id
    );
    setAudioMediaId(matching?.id ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.metadata?.scene_id, readyAudios.length]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (tab === 'audio') {
        if (!audioMediaId) {
          toast({ text: 'Select an audio clip', level: 'error' });
          setIsSubmitting(false);
          return;
        }
        await API.lipSyncMedia(clipId, video.id, { audio_media_id: audioMediaId });
      } else {
        if (!text.trim()) {
          toast({ text: 'Enter text to speak', level: 'error' });
          setIsSubmitting(false);
          return;
        }
        await API.lipSyncMedia(clipId, video.id, { text: text.trim(), voice, speed, pitch });
      }
      toast({ text: 'Lip sync started — new video will appear when ready', level: 'success' });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({ text: `Lip sync failed: ${error.message}`, level: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Lip Sync" size="md">
      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        <button
          type="button"
          className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
            tab === 'speech'
              ? 'bg-white/10 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
          onClick={() => setTab('speech')}
        >
          Text + Voice
        </button>
        <button
          type="button"
          className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
            tab === 'audio'
              ? 'bg-white/10 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
          onClick={() => setTab('audio')}
        >
          Use Audio Clip
        </button>
      </div>

      {tab === 'speech' && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Dialogue text</label>
            <textarea
              className="w-full bg-slate-800 text-white text-sm rounded-md px-3 py-2 border border-white/10 focus:outline-none focus:border-white/30 resize-none"
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What should the character say?"
            />
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Voice</label>
            <select
              className="w-full bg-slate-800 text-white text-sm rounded-md px-3 py-2 border border-white/10 focus:outline-none focus:border-white/30"
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
            >
              {VOICES.map((v) => (
                <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-xs text-slate-400 mb-1">Speed ({speed.toFixed(1)}x)</label>
              <input
                type="range"
                min={0.5}
                max={2.0}
                step={0.1}
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
                className="w-full accent-white"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-slate-400 mb-1">Pitch ({pitch > 0 ? '+' : ''}{pitch})</label>
              <input
                type="range"
                min={-10}
                max={10}
                step={1}
                value={pitch}
                onChange={(e) => setPitch(parseFloat(e.target.value))}
                className="w-full accent-white"
              />
            </div>
          </div>
        </div>
      )}

      {tab === 'audio' && (
        <div>
          <label className="block text-xs text-slate-400 mb-1">Audio clip</label>
          {readyAudios.length === 0 ? (
            <p className="text-xs text-slate-500 italic">
              No ready audio clips in this clip. Generate audio first or use Text + Voice tab.
            </p>
          ) : (
            <select
              className="w-full bg-slate-800 text-white text-sm rounded-md px-3 py-2 border border-white/10 focus:outline-none focus:border-white/30"
              value={audioMediaId}
              onChange={(e) => setAudioMediaId(e.target.value)}
            >
              <option value="">Select audio...</option>
              {readyAudios.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.prompt || a.id.slice(0, 8)}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-white/10 mt-4">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          <span className="flex items-center gap-1.5">
            {isSubmitting ? 'Starting...' : 'Start Lip Sync'}
            {!isSubmitting && display === 'credits' && lipSyncCostCredits != null && (
              <span className="text-yellow-300 font-bold">⚡ ~{lipSyncCostCredits.toLocaleString()}</span>
            )}
          </span>
        </Button>
      </div>
    </Modal>
  );
};

export default LipSyncModal;
